import { useEffect, useState, useRef, useCallback } from "react";
import { useAbortController } from "../../hooks/useAbortController";
import { canEditClinkField, isAdminUser } from "../../utils/UserHelper";
import { App, CampaignsResponseDto, CampaignsResponseDtoCampaign, CampaignsResponseDtoUnconfiguredCampaign, Clink, ClinkResponseDto, CountryOption, Message, MessageType, PayoutMode, PublisherOrSiteOption, PublisherRecord, ResponseStatus, User, Validation } from "../../utils/types";
import { Alert, Button, Card, Col, Form, Modal, OverlayTrigger, Row, ToggleButton, ToggleButtonGroup, Tooltip } from "react-bootstrap";
import { Users } from "../../services/Users";
import { Clinks } from "../../services/Clinks";
import { Campaigns } from "../../services/Campaigns";
import { TrafficSources } from "../../services/TrafficSources";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "../modal/ModalHeader";
import ModalNotificaton from "../notifications/ModalNotificaton";
import COUNTRIES from "../autocompletes/countries/countries";
import CampaignsTable from "./CampaignsTable";
import Loading from "../../utils/Loding";
import ModalTrackingLinks from "./ModalTrackingLinks";
import ModalHistory from "./ModalHistory";
import InfoIcon from "./info/InfoIcon";
import TimeAgo from "timeago-react";
import CountriesAutocomplete from "../autocompletes/countries/CountriesAutocomplete";
import AutocompleteSingle from "../library/AutocompleteSingle";
import ValidationsWrapper from "../validation/ValidationsWrapper";
import OverrideCounterBidingTooltip from "./info/OverrideCounterBidingTooltip";
import HydrateCounterBidingTooltip from "./info/HydrateCounterBidingTooltip";
import { PagesHelper } from "../../common/PagesHelper";
import './ClinkForm.css';

const faCircleExclamationIcon = <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "red" }} fixedWidth></FontAwesomeIcon>;

const getAllowCounterBid = (clink:Clink, useDefaultIfNotSet:boolean) => {
	if (clink.allowCounterBid === "allow") {
		return true;
	}
	if (clink.allowCounterBid === "disable") {
	  return false;
	}
	if (useDefaultIfNotSet) {
		return "default"
	}
	return null;
};

const getAddedCampaigns = (campaigns:CampaignsResponseDtoCampaign[], unconfiguredCampaigns:CampaignsResponseDtoUnconfiguredCampaign[]) => {
  const campaignsAdded = campaigns.filter((c) => c.added).map((camp) => camp.supplySource + ':' + camp.campaignID + ':' + camp.supplySourceName);
  const unconfiguredCampaignsAdded = unconfiguredCampaigns.filter((c) => c.added).map((camp) => camp.source + ':' + camp.campaign_id + ':' + camp.source_name);
  return [campaignsAdded, unconfiguredCampaignsAdded].filter(added => added.length > 0).join(",");
};

const getAllowCounterBidOverride = (clink:Clink, useDefaultIfNotSet: boolean) => {
  if (clink.allowCounterBidOverride === "allow") {
    return true;
  }
  if (clink.allowCounterBidOverride === "disable") {
    return false;
  }
  if (useDefaultIfNotSet) {
    return "default"
  }
  return null;
};


interface ModalEditClinkProps {
  app: App;
  clinkId: string;
  closeEditClink: () => void;
  reloadClink: (clinkId:string, apply?:boolean) => void;
}

function ModalEditClink(props:ModalEditClinkProps) {
  const { app, clinkId, closeEditClink, reloadClink } = props;
  const { createController } = useAbortController();
  const [editionSuccess, setEditionSuccess] = useState<boolean>(false);
  const [showEditClinkFormData, setShowEditClinkFormData] = useState<boolean>(true);
  const [users, setUsers] = useState([]);
  const [clinkResponseDto, setClinkResponseDto] = useState<ClinkResponseDto>(null);
  const [campaignsResponseDto, setCampaignsResponseDto] = useState<CampaignsResponseDto>(null);
  const [messages, setMessages] = useState([]);
  const [showModalNotification, setShowModalNotification] = useState(false);

  const [clink, setClink] = useState<Clink>(null);
  const [showTrackingLinks, setShowTrackingLinks] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [validations, setValidations] = useState<Validation[]>([
    { "owner": false, "countries": false, "dailyCap": false, "payout": false }
  ]);
  const [clinkableTrafficSources, setClinkableTrafficSources] = useState<PublisherOrSiteOption[]>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [validationMessage, setValidationMessage] = useState("");
  const [payoutMode, setPayoutMode] = useState("");
  const [allowRevShare, setAllowRevShare] = useState(false);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  const [campaignsAreDirty, setCampaignsAreDirty] = useState<boolean>(false);

  const countriesRef = useRef(null);
  const payoutRef = useRef(null);
  const dailyCapRef = useRef(null);

  const checkValidations = useCallback(() => {
    if (canEditClinkField('ALL_FIELDS')) {
      const unconfiguredCampaignsAdded = campaignsResponseDto && campaignsResponseDto.unconfiguredCampaigns ? campaignsResponseDto.unconfiguredCampaigns.filter((unconfiguredCampaign:any) => unconfiguredCampaign.added) : [];
      const campaignsAdded = campaignsResponseDto && campaignsResponseDto.campaigns ? campaignsResponseDto.campaigns.filter((campaign:any) => campaign.added) : [];
      const hasUnselectedCampaigns = campaignsAdded.length === 0 && unconfiguredCampaignsAdded.length === 0;
      if (hasUnselectedCampaigns) {
        setValidationMessage("No campaigns selected.");
        setDisabled(true);
        return;
      }

      if (validations.length) {
        if (validations[0]["owner"] !== true) {
          setValidationMessage("No owner selected.");
          setDisabled(true);
          return;
        }
    
        if (validations[0]["countries"] !== true) {
          setValidationMessage("No country selected.");
          setDisabled(true);
          return;
        }
    
        if (validations[0]["dailyCap"] !== true) {
          setValidationMessage("Invalid daily cap.");
          setDisabled(true);
          return;
        }
        if (validations[0]["payout"] !== true) {
          setValidationMessage("Invalid payout.");
          setDisabled(true);
          return;
        }
      }
    }

    setValidationMessage("");
    setDisabled(false);
  }, [ validations, campaignsResponseDto ]);

  useEffect(() => {
    Clinks.getClink(clinkId, setClinkResponseDto);
    if (canEditClinkField('ALL_FIELDS')) {
      Users.getAssignableUsers(setUsers);
    }
    return () => setUsers(null);
  }, [clinkId]);

  useEffect(() => {
    if (canEditClinkField('ALL_FIELDS') && clinkResponseDto && clinkResponseDto.clink && Object.keys(clinkResponseDto.clink).length) {
      Campaigns.getCampaignsForAppID(clinkResponseDto, setCampaignsResponseDto);
    }

    if (clinkResponseDto && clinkResponseDto.clink) {
      setClink(clinkResponseDto.clink);

      if (clinkResponseDto.clink.useRevShareP2p) {
        setPayoutMode(PayoutMode.REV_SHARE);
      } else if (clinkResponseDto.clink.p2p > 0) {
        setPayoutMode(PayoutMode.FIXED);
      } else {
        setPayoutMode(PayoutMode.NO_PAYOUT);
      }

      TrafficSources.getTrafficSourcesClinkables(createController(), setClinkableTrafficSources);
    }
  }, [clinkResponseDto, createController]);

  useEffect(() => {
    if (clinkableTrafficSources && clinkResponseDto?.clink) {
      let newAllowRevShare = false;
      const trafficSource = clinkableTrafficSources.find((t) => t.record.apptapPublisherId === clinkResponseDto.clink.pubId);
      if (trafficSource) {
        const site = (trafficSource.record as PublisherRecord).publisherSites.find((site) => site.apptapSiteId === clinkResponseDto.clink.siteId);
        if (site && site.publisherRevShare && site.publisherRevShare > 0) {
          newAllowRevShare = true;
        }
      }
      setAllowRevShare(newAllowRevShare);
    }
  }, [clinkResponseDto, clinkableTrafficSources]);

  useEffect(() => {
    checkValidations();
  }, [checkValidations, clink]);

  useEffect(() => {
    const newCountryOptions:CountryOption[] = [];
    const campaignsAdded = campaignsResponseDto && campaignsResponseDto.campaigns ? campaignsResponseDto.campaigns.filter((campaign) => campaign.added) : [];
    campaignsAdded.forEach((campaign) => {
      const countryIds = campaign.countries.split(",");
      countryIds.forEach((id:string) => {
        const country = COUNTRIES.find((c) => c.id === id);
        if (country && !newCountryOptions.includes(country)) {
          newCountryOptions.push(country);
        }
      })
    })

    if (!campaignsAreDirty && clink?.countries) {
      clink.countries.forEach((clinkCountry) => {
        const country = COUNTRIES.find((c) => c.id === clinkCountry.id);
        if (!newCountryOptions.includes(country)) {
          newCountryOptions.push(country);
        }
      });
    }

    if (JSON.stringify(newCountryOptions) !== JSON.stringify(countryOptions)) {
      setCountryOptions(newCountryOptions);
      setClink((prevClink:Clink) =>{
        return {
          ...prevClink,
          countries: newCountryOptions
        };
      });
    }
  }, [campaignsResponseDto, countryOptions, campaignsAreDirty, clink]);

  if (clinkResponseDto && clinkResponseDto.status === ResponseStatus.LOADING) return <Loading />;
  if (!clinkResponseDto || !clink || Object.keys(clink).length === 0 || clinkResponseDto.status === ResponseStatus.ERROR) {
    return (
      <Alert variant={MessageType.DANGER}>
        <span>{clinkResponseDto && clinkResponseDto.error ? clinkResponseDto.error : "Error loading clink"}</span>
      </Alert>
    );
  }

  const handleClose = () => {
    setShowEditClinkFormData(true);
    closeEditClink();
  }

  const handleOwnerChange = (users:User[]) => {
    const newOwner = users && users.length ? users[0].loginEmail : null;
    handleClinkPropChange("owner", newOwner);
  }

  const handleClinkPropChange = (prop:string, value:any) => {
    setClink((prevClink:Clink) =>{
      return {
        ...prevClink,
        [prop]: value
      };
    });
  }

  const handlePayoutModeChange = (newPayoutMode:string) => {
    const newUseRevShareP2p = newPayoutMode === PayoutMode.REV_SHARE;
    handleClinkPropChange("useRevShareP2p", newUseRevShareP2p);
    if (newPayoutMode !== PayoutMode.FIXED) {
      handleClinkPropChange("p2p", 0);
    }
    setPayoutMode(newPayoutMode);
  }

  const handleCountriesKeyDown = (e:any) => {
    if (e.key === 'Enter') {
      const selectedIds = (e.target as HTMLInputElement).value.split(",");
      const countries:CountryOption[] = [...clink.countries];
      selectedIds.forEach((id:string) => {
        const country = countryOptions.find((country) => country.id.toLowerCase() === id.toLowerCase());
        if (country && !countries.includes(country)) {
          countries.push(country);
        }
      })
      handleClinkPropChange("countries", countries);
      const prevState = {...countriesRef.current.state};
      countriesRef.current.setState({...prevState, text: ""});
    }
  }

  const handleUpdate = async () => {
    const newMessages:Message[] = [];
    const updatedClink = clink;

    let updateParams:any = {};
    setShowModalNotification(true);

    if (!canEditClinkField('ALL_FIELDS') && (canEditClinkField('COUNTER_BID') || canEditClinkField('STATUS'))) {
      // Currently for non-admins the only attributes to potentially edit are status and the allowCounterBid (for partner) setting.

      updateParams = {
        clinkid: updatedClink.clinkId,
        method: "updateClink"
      }

      if (canEditClinkField('COUNTER_BID')) {
        updateParams.allowCounterBid = getAllowCounterBid(updatedClink, true);
      }
      if (canEditClinkField('STATUS')) {
        updateParams.status = updatedClink.status;
      }

      const result:any = await Clinks.updateCLink(updateParams);
      if (!result.error) {
        newMessages.push({ message: `cLink ${result.clinkid} Successfully Updated`, type: MessageType.INFO });
        setEditionSuccess(true);        
        setShowEditClinkFormData(false);
      } else {
        newMessages.push({ message: `Could not update cLink ${updatedClink.clinkId}: ${result.error?.message}`, type: MessageType.DANGER });
      }
    } else {
      const updatedCampaigns = campaignsResponseDto.campaigns;
      const updatedunconfiguredCampaigns = campaignsResponseDto.unconfiguredCampaigns;
  
      updateParams = {
        method: "addClink",
        clinkid: updatedClink.clinkId,
        status: updatedClink.status,
        pubid: updatedClink.pubId,
        siteid: updatedClink.siteId,
        appid: updatedClink.appId,
        campids: getAddedCampaigns(updatedCampaigns, updatedunconfiguredCampaigns),
        platforms: updatedClink.platforms,
        ignorePlatforms: updatedClink.ignorePlatforms,
        p2p: updatedClink.p2p,
        minPayout: updatedClink.minPayout,
        countries: updatedClink.countries ? updatedClink.countries.map((country:CountryOption) => country.id).join(" ") : "",
        dailyCap: updatedClink.dailyCap,
        incent: updatedClink.incent,
        owner: updatedClink.owner,
        useRevShareP2P: updatedClink.useRevShareP2p,
        allowCounterBidOverride: getAllowCounterBidOverride(updatedClink, false),
        allowCounterBid: getAllowCounterBid(updatedClink, false)
      };

      const result:any = await Clinks.updateCLink(updateParams);
      if (result && result.clinkid === updatedClink.clinkId) {
        newMessages.push({ message: `cLink ${result.clinkid} Successfully Updated`, type: MessageType.INFO });
        setEditionSuccess(true);        
        setShowEditClinkFormData(false);
        reloadClink(result.clinkid);
      } else {
        newMessages.push({ message: `Could not update cLink ${updatedClink.clinkId}`, type: MessageType.DANGER });
      }
    }
    setMessages(newMessages);
  }

  return (
    <>
      <Modal
        dialogClassName="modal-clink-edit"
        show={showEditClinkFormData} onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        style={{ maxHeight: "95vh" }}
      >
        <ModalHeader title="cLink Edit" useIcon={true} icon={app.icon ? app.icon : null} />
        <Modal.Body style={{ maxHeight: "calc(95vh - 200px)", overflowY: "auto" }}>
          <div>
            <Row>
              <Col>
                <p><strong>cLink ID: </strong>{clink.clinkId}</p>
              </Col>
              <Col>
                <p><strong>App ID: </strong>{clink.appId}</p>
              </Col>
              <Col>
                <p><strong>Traffic Source: </strong>{PagesHelper.getTrafficSourceLabel(clink.pubId, clink.siteId, clinkableTrafficSources)} ({clink.pubId}:{clink.siteId})</p>
              </Col>
            </Row>
            <Row>
              <Col>
                <OverlayTrigger
                  key="created"
                  placement="auto"
                  overlay={( props ) => {
                    return (                        
                      <Tooltip {...props} id="created">
                        {clink.created?.toISOString()}
                      </Tooltip>
                    );
                  }
                }>
                  <span><strong>Created: </strong><TimeAgo datetime={ clink.created?.toDate()} locale='en_US'/></span>
                </OverlayTrigger>
              </Col>
              <Col>
                <OverlayTrigger
                  key="updated"
                  placement="auto"
                  overlay={( props ) => {
                    return (                        
                      <Tooltip {...props} id="created">
                        {clink.lastUpdate?.toISOString()}
                      </Tooltip>
                    );
                  }
                }>
                  <span><strong>Last Updated: </strong><TimeAgo datetime={ clink.lastUpdate?.toDate()} locale='en_US'/></span>
                </OverlayTrigger>
              </Col>
              <Col>
                {
                  canEditClinkField('ALL_FIELDS') ?
                    <p><strong>Incent Placement: </strong>{clink.incent ? "Yes" : "No"}</p>
                  : null
                }
              </Col>
            </Row>
          </div>

          <hr />

          { // admin
            canEditClinkField('ALL_FIELDS') ?
            <div>
              <Row>
                <Col xl={1}>
                  <p><strong>Owner </strong></p>
                </Col>
                <Col xl={2}>
                  <ValidationsWrapper
                    name={"owner"}
                    required
                    setValidations={setValidations}
                    defaultDirty
                    isAutocomplete
                    value={users.filter((user:any) => user.loginEmail === clink.owner)}
                  >
                    <AutocompleteSingle
                      id="fowner"
                      placeholder="No Owner"
                      labelKey={(user:any) => user.name}
                      options={users}
                      onChange={handleOwnerChange}
                      selected={users.filter((user) => user.loginEmail === clink.owner)}
                      style={{ fontSize: 12 }}
                    />
                  </ValidationsWrapper>
                </Col>
                <Col xl={1}>
                  <p><strong>Daily Conv. Cap</strong></p>
                </Col>
                <Col xl={3}>
                  <ValidationsWrapper
                    name={"dailyCap"}
                    min={1}
                    max={1000000}
                    required
                    defaultDirty
                    setValidations={setValidations}
                    value={clink.dailyCap}
                  >
                    <Form.Control
                      as="input"
                      type="number"
                      size="sm"
                      onFocus={() => dailyCapRef.current?.select()}
                      onChange={(e) => handleClinkPropChange("dailyCap", e.target.value)}
                      ref={dailyCapRef}
                      value={clink.dailyCap}
                    />
                  </ValidationsWrapper>
                </Col>
                <Col xl={2} style= {{ display: "flex", alignItems: "baseline" }}>
                  <p>
                    <strong>Counter Bidding - Admin Override&nbsp;</strong>
                    <InfoIcon id={"overrideCounterBidingTooltip"}><OverrideCounterBidingTooltip /></InfoIcon>
                    &nbsp;
                  </p>
                </Col>
                <Col xl={3}>

                  <ToggleButtonGroup type="radio" name="allowCounterBidOverride" value={clink.allowCounterBidOverride} onChange={(newAllowCounterBidOverride:string) => handleClinkPropChange("allowCounterBidOverride", newAllowCounterBidOverride)}>
                    <ToggleButton value="default" size="sm" className={clink.allowCounterBidOverride === "default" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Default</ToggleButton>
                    <ToggleButton value="allow" size="sm" className={clink.allowCounterBidOverride === "allow" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Allow <span style={{ fontSize: "10px" }}>(Ignore Partner)</span></ToggleButton>
                    <ToggleButton value="disable" size="sm" className={clink.allowCounterBidOverride === "disable" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Disable <span style={{ fontSize: "10px" }}>(Ignore Partner)</span></ToggleButton>
                  </ToggleButtonGroup>
                </Col>
              </Row>
              <Row>
                {
                  (canEditClinkField('STATUS')) ?
                  <>
                    <Col xl={1}>
                      <p><strong>Status</strong></p>
                    </Col>
                    <Col xl={2}>
                      <ToggleButtonGroup type="radio" name="status" value={clink.status} onChange={(newStatusValue:string) => handleClinkPropChange("status", newStatusValue)}>
                        <ToggleButton id="tbg-btn-1" size="sm" value="active" className={clink.status === "active" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Active</ToggleButton>
                        <ToggleButton id="tbg-btn-2" size="sm" value="paused" className={clink.status === "paused" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Paused</ToggleButton>
                        <ToggleButton id="tbg-btn-3" size="sm" value="ended" className={clink.status === "ended" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Ended</ToggleButton>
                      </ToggleButtonGroup>
                    </Col>
                  </>
                  :
                  <>
                    <Col xl={1}>
                      <p><strong>Status</strong></p>
                    </Col>
                    <Col xl={2}>
                      <div style={{ textTransform: "capitalize" }}>{clink.status}</div>
                    </Col>
                  </>
                }

                <Col xl={1}>
                  <p><strong>Payout to Partner</strong></p>
                </Col>
                <Col xl={3}>
                  <div style={{ display: "flex", alignItems: "baseline" }}>
                    <ToggleButtonGroup type="radio" name="payoutMode" value={payoutMode} onChange={handlePayoutModeChange}>
                      {
                        allowRevShare ? <ToggleButton value="revShare" className={payoutMode === "revShare" ? "active-toggle-button" : "inactive-toggle-button"} size="sm" style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Rev Share</ToggleButton> : null
                      }
                      <ToggleButton value="noPyout" size="sm" className={payoutMode === "noPyout" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>No payout</ToggleButton>
                      <ToggleButton value="fixed" size="sm" className={payoutMode === "fixed" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Fixed</ToggleButton>
                    </ToggleButtonGroup>

                    <ValidationsWrapper
                      name={"payout"}
                      required={payoutMode === "fixed"}
                      setValidations={setValidations}
                      value={clink.p2p}
                      style={{ display: "flex", width: "70px" }}
                    >
                      <Form.Control
                        as="input"
                        type="number"
                        size="sm"
                        step="0.01"
                        disabled={payoutMode !== "fixed"}
                        onFocus={() => payoutRef.current?.select()}
                        onChange={(e) => handleClinkPropChange("p2p", e.target.value)}
                        value={clink.p2p}
                        ref={payoutRef}
                        style={{ maxWidth: "90px", fontSize: 12 }}
                      />
                    </ValidationsWrapper>
                  </div>
                </Col>
                <Col xl={2}>
                  <p><strong>Counter Bidding - Partner </strong></p>
                </Col>
                <Col xl={2}>
                  <div style={{ textTransform: "capitalize" }}>&nbsp;{clink.allowCounterBid}</div>
                </Col>
              </Row>

              <Row>
                {/* support_tags/notes
                <Col xl={1}>
                  <p><strong>Tags</strong></p>
                </Col>
                <Col xl={2}>
                  <TagsAutocomplete
                    onChange={(e:any) => handleClinkPropChange("tags", e.map((tag:any) => tag.label ? tag.label : tag))}
                    onKeyDown={(e) => handleTagsKeyDown(e)}
                    options={mostFrequentTags}
                    selected={clink.tags}
                    reactRef={tagsRef}
                  />
                </Col>
                */}
                <Col xl={1}>
                  <p><strong>Countries</strong></p>
                </Col>
                <Col xl={2}>
                  <ValidationsWrapper
                    name={"countries"}
                    required
                    defaultDirty
                    isAutocomplete
                    setValidations={setValidations}
                    value={clink.countries}
                    >
                    <CountriesAutocomplete
                      onKeyDown={(e:React.SyntheticEvent) => handleCountriesKeyDown(e)}
                      onChange={(e:React.SyntheticEvent) => handleClinkPropChange("countries", e)}
                      options={countryOptions}
                      selected={clink.countries}
                      reactRef={countriesRef}
                      multiple
                      style={{ width: "220px" }}
                    />
                  </ValidationsWrapper>
                </Col>
                <Col xl={1}>
                  <p><strong>Platforms</strong></p>
                </Col>
                <Col xl={3}>
                  <ToggleButtonGroup type="radio" name="ignorePlatforms" value={clink.ignorePlatforms.toString()} onChange={(e) => handleClinkPropChange("ignorePlatforms", e === "true")}>
                    <ToggleButton value="true" size="sm" className={clink.ignorePlatforms.toString() === "true" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Allow All</ToggleButton>
                    <ToggleButton value="false" size="sm" className={clink.ignorePlatforms.toString() === "false" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>{clink.platforms.split(",").join(" / ")}</ToggleButton>
                  </ToggleButtonGroup>
                </Col>
              </Row>

            </div>
          : // non admin
            <div>
              <Row>
                <Col xl={3}>
                  {
                    (canEditClinkField('STATUS')) ?
                    <>
                      <p><strong>Status:</strong></p>
                      <ToggleButtonGroup type="radio" name="status" value={clink.status} onChange={(newStatusValue:string) => handleClinkPropChange("status", newStatusValue)}>
                        <ToggleButton id="tbg-btn-1" size="sm" value="active" className={clink.status === "active" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Active</ToggleButton>
                        <ToggleButton id="tbg-btn-2" size="sm" value="paused" className={clink.status === "paused" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Paused</ToggleButton>
                        <ToggleButton id="tbg-btn-3" size="sm" value="ended" className={clink.status === "ended" ? "active-toggle-button" : "inactive-toggle-button"} style={{ fontSize: "12px" }}>Ended</ToggleButton>
                      </ToggleButtonGroup>
                    </>
                    :
                    <>
                      <p><strong>Status</strong></p>
                      <div style={{ textTransform: "capitalize" }}>{clink.status}</div>
                    </>
                  }
                </Col>
                <Col xl={3}>
                  <p><strong>Daily Conv. Cap:</strong></p>
                  <div>{clink.dailyCap}</div>
                </Col>
                <Col xl={3}>
                  <p><strong>Countries:</strong></p>
                  <div>{clink.countries.map((country => <span key={country.id}>{country.name}</span>))}</div>
                </Col>
                <Col xl={3}>
                  {
                    canEditClinkField('COUNTER_BID') &&
                      <>
                        <p><strong>Campaign Counter Bidding (Hydrate): </strong><InfoIcon><HydrateCounterBidingTooltip /></InfoIcon></p>
                        {
                          clink.allowCounterBidOverride !== 'default' &&
                          <label>
                            <i>
                              - Currently overridden by system to 
                              <b><span style={{ textTransform: "capitalize" }}>{clink.allowCounterBidOverride}</span></b>.
                            </i>
                          </label>
                        }
                      </>
                  }
                  <div style={{ textTransform: "capitalize" }}>{clink.allowCounterBid}</div>
                  {
                    canEditClinkField('COUNTER_BID') && 
                    <ToggleButtonGroup type="radio" name="allowCounterBid" value={clink.allowCounterBid} onChange={(newAllowCounterBid:string) => handleClinkPropChange("allowCounterBid", newAllowCounterBid)}>
                      <ToggleButton value="default" size="sm" className={clink.allowCounterBid === "default" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is based on current publisher/campaign configuration or if the hydrate parameter is set.">Default</ToggleButton>
                      <ToggleButton value="allow" size="sm" className={clink.allowCounterBid === "allow" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is allowed, regardless of current publisher configuration or hydrate parameter setting, if campaign is configured to allow. Can be potentially be overridden by the system clink counter bidding setting.">Allow</ToggleButton>
                      <ToggleButton value="disable" size="sm" className={clink.allowCounterBid === "disable" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is disabled, regardless of any other configuration setting or hydrate parameter seting.">Disable</ToggleButton>
                    </ToggleButtonGroup>
                  }
                </Col>
              </Row>
            </div>
          }
          <hr />
          {
            canEditClinkField('ALL_FIELDS') &&
            <>
              {
                (campaignsResponseDto && campaignsResponseDto.unconfiguredCampaigns.length) ?
                <div>
                  <CampaignsTable
                    headerTitle={
                      <span>
                        Unconfigured Campaigns <InfoIcon id={"unconfiguredCampaignsTooltip"}>
                          <>
                            <span>Campaigns that are in the cLink configuration but not currently available in the live campaign feed.</span>
                            <ul>
                              <li>These campaigns may have been paused or removed in Pluto</li>
                              <li>Campaigns can remain cached in Jupiter for up to 60 days after removal</li>
                              <li>You can replace them by selecting a different campaign from the "Possible Campaigns" section below</li>
                            </ul>
                          </>
                        </InfoIcon>
                      </span>
                    }
                    campaignsResponseDto={campaignsResponseDto}
                    setCampaignsResponseDto={setCampaignsResponseDto}
                    unconfigured={true}
                    setCampaignsAreDirty={setCampaignsAreDirty}
                  />
                </div>
                : null
              }
              <div>
                <CampaignsTable
                  headerTitle="Possible Campaigns"
                  campaignsResponseDto={campaignsResponseDto}
                  setCampaignsResponseDto={setCampaignsResponseDto}
                  setCampaignsAreDirty={setCampaignsAreDirty}
                />
              </div>
            </>
          }
        </Modal.Body>
        <Modal.Footer>
          <Card style={{ width: "100%", margin: 0 }}>
            <Card.Body style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0.5rem" }}>
              <div>{ disabled ? <Alert style={{ margin: 0 }}>{faCircleExclamationIcon} {validationMessage}</Alert> : null }</div>
              <div style={{ display: "flex", justifyContent: "end" }}>
                { (isAdminUser()) ? <Button variant="info" style={{ margin: "5px" }} onClick={() => setShowHistory(true)}>History</Button> : null }
                <Button variant="info" style={{ margin: "5px" }} onClick={() => setShowTrackingLinks(true)}>Tracking Links</Button>
                <Button variant="primary" style={{ margin: "5px" }} disabled={disabled} onClick={handleUpdate}>Update</Button>
                <Button variant="secondary" style={{ margin: "5px" }} onClick={handleClose}>Cancel</Button>
              </div>
            </Card.Body>
          </Card>
          <ModalTrackingLinks
            showTrackingLinks={showTrackingLinks}
            setShowTrackingLinks={setShowTrackingLinks}
            clinkid={clink.clinkId}
            previewLink={clink.previewLink}
            app={app}
          />
          <ModalHistory
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            clink={clink}
            app={app}
          />
        </Modal.Footer>
      </Modal>
      <ModalNotificaton
        title="Update Clink"
        messages={messages}
        show={showModalNotification}
        setShow={setShowModalNotification}
        handleParentClose={editionSuccess ? closeEditClink : null}
      />
    </>
  );
}

export default ModalEditClink;